import { useState } from 'react'
import { Link, createFileRoute } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { ArrowRight, ClipboardCheck, Plus } from 'lucide-react'
import { DashboardLayout } from '@/components/layout'
import { atsNav } from '@/lib/atsNav'
import { currentTenant, currentUser } from '@/lib/mocks'
import { type DashboardPeriod, getDashboard } from '@/lib/api'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { PageHeader } from '@/lib/atsUi'
import { cn } from '@/lib/utils'

const PERIODS: { value: DashboardPeriod; label: string }[] = [
  { value: 'week', label: 'Week' },
  { value: 'month', label: 'Month' },
  { value: 'quarter', label: 'Quarter' },
  { value: 'all', label: 'All' },
]
const periodWord: Record<DashboardPeriod, string> = { week: 'this week', month: 'this month', quarter: 'this quarter', all: 'all time' }
const nf = (n: number) => n.toLocaleString()

function ago(iso: string): string {
  const then = new Date(iso).getTime()
  if (Number.isNaN(then)) return ''
  const mins = Math.max(0, Math.round((Date.now() - then) / 60000))
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.round(mins / 60)
  if (hrs < 24) return `${hrs}h ago`
  const days = Math.round(hrs / 24)
  return `${days}d ago`
}

// The delivery funnel: demand (positions) → matching (screened/submitted/interviews) → outcome (placements).
const FUNNEL: { key: 'totalPositions' | 'openPositions' | 'screened' | 'submitted' | 'interviews' | 'placements'; label: string; tone: 'demand' | 'mid' | 'win'; sub?: string }[] = [
  { key: 'totalPositions', label: 'Total positions', tone: 'demand', sub: 'all roles' },
  { key: 'openPositions', label: 'Open', tone: 'demand', sub: 'pending' },
  { key: 'screened', label: 'Screened', tone: 'mid' },
  { key: 'submitted', label: 'Submitted', tone: 'mid' },
  { key: 'interviews', label: 'Interviews', tone: 'mid' },
  { key: 'placements', label: 'Placements', tone: 'win' },
]

function FunnelRibbon({ period }: { period: DashboardPeriod }) {
  const { data } = useQuery({ queryKey: ['dashboard', period], queryFn: () => getDashboard(period) })
  return (
    <div className="flex gap-2">
      {FUNNEL.map((s) => {
        const v = data?.funnel[s.key] ?? null
        const numColor = s.tone === 'demand' ? 'text-info' : s.tone === 'win' ? 'text-success' : 'text-foreground'
        return (
          <div key={s.key} className="flex-1 rounded-lg bg-muted/40 p-3 text-center">
            <div className="text-[10px] uppercase leading-tight tracking-wide text-muted-foreground">{s.label}</div>
            {!data ? (
              <div className="mt-1 text-xl font-medium text-muted-foreground">·</div>
            ) : (
              <div className={cn('mt-0.5 text-2xl font-medium', numColor)}>{nf(v ?? 0)}</div>
            )}
            {s.sub && <div className="mt-0.5 text-[10px] text-muted-foreground">{s.sub}</div>}
          </div>
        )
      })}
    </div>
  )
}

function GroupCards({ period }: { period: DashboardPeriod }) {
  const { data } = useQuery({ queryKey: ['dashboard', period], queryFn: () => getDashboard(period) })
  const num = (n: number | null | undefined) => (n == null ? '—' : nf(n))
  return (
    <div className="grid gap-4 sm:grid-cols-3">
      <GroupCard title="Roles · demand" titleColor="text-info" rows={[
        ['Open positions', num(data?.roles.open)],
        [`New ${periodWord[period]}`, num(data?.roles.newThisPeriod)],
        ['Clients', num(data?.roles.clients)],
      ]} />
      <GroupCard title="Delivery" rows={[
        ['Screened', num(data?.delivery.screened)],
        ['Shortlisted', num(data?.delivery.shortlisted)],
        ['Placements', num(data?.delivery.placements)],
      ]} />
      <GroupCard title="Talent · supply" titleColor="text-success" rows={[
        ['Candidates', num(data?.talent.totalCandidates)],
        [`Added ${periodWord[period]}`, num(data?.talent.addedThisPeriod)],
        ['Avg fit score', num(data?.talent.avgFit)],
      ]} />
    </div>
  )
}

function GroupCard({ title, titleColor, rows }: { title: string; titleColor?: string; rows: [string, string][] }) {
  return (
    <Card>
      <CardContent className="pt-4 pb-4">
        <h4 className={cn('mb-2 text-xs font-medium uppercase tracking-wide', titleColor ?? 'text-muted-foreground')}>{title}</h4>
        {rows.map(([k, v]) => (
          <div key={k} className="flex justify-between py-1 text-sm">
            <span className="text-muted-foreground">{k}</span>
            <span className="font-medium text-foreground">{v}</span>
          </div>
        ))}
      </CardContent>
    </Card>
  )
}

// Real "needs you": the most recently screened roles whose shortlist is ready for recruiter review.
function NeedsReview({ period }: { period: DashboardPeriod }) {
  const { data } = useQuery({ queryKey: ['dashboard', period], queryFn: () => getDashboard(period) })
  const items = data?.needsReview ?? []
  return (
    <section>
      <div className="mb-2 flex items-center gap-2">
        <ClipboardCheck className="h-4 w-4 text-primary" />
        <h2 className="text-sm font-medium text-foreground">Shortlists ready to review</h2>
        {items.length > 0 && <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">{items.length}</span>}
      </div>
      <Card>
        <CardContent className="p-0">
          {items.length === 0 ? (
            <p className="px-4 py-8 text-center text-sm text-muted-foreground">
              {data ? <>No shortlists waiting. <Link to="/screen" className="text-primary underline">Screen a role</Link> to build one.</> : 'Loading…'}
            </p>
          ) : (
            <div className="divide-y divide-border">
              {items.map((r) => (
                <Link key={r.jobId} to="/jobs/$jobId" params={{ jobId: r.jobId }} className="flex items-center gap-3 px-4 py-3 transition-colors hover:bg-muted/40">
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-foreground">{r.title}</p>
                    <p className="mt-0.5 text-xs text-muted-foreground">Screened {ago(r.screenedAt)}</p>
                  </div>
                  <span className="shrink-0 rounded-full border border-border bg-muted/40 px-2 py-0.5 text-xs text-muted-foreground tabular-nums">{nf(r.shortlistCount)} shortlisted</span>
                  <ArrowRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                </Link>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </section>
  )
}

function Dashboard() {
  const [period, setPeriod] = useState<DashboardPeriod>('month')

  return (
    <DashboardLayout title="Dashboard" navSections={atsNav} tenant={currentTenant} user={currentUser}>
      <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <PageHeader title="Dashboard" subtitle={`Your delivery funnel — ${periodWord[period]}.`} />
        <div className="flex items-center gap-2">
          <div className="flex gap-1 rounded-md border border-border bg-card p-0.5">
            {PERIODS.map((p) => (
              <button
                key={p.value}
                onClick={() => setPeriod(p.value)}
                className={cn('rounded px-2.5 py-1 text-xs transition-colors', period === p.value ? 'bg-info/10 font-medium text-info' : 'text-muted-foreground hover:text-foreground')}
              >
                {p.label}
              </button>
            ))}
          </div>
          <Button size="sm" asChild>
            <Link to="/jobs"><Plus className="h-4 w-4" /> New job</Link>
          </Button>
        </div>
      </div>

      <div className="space-y-6">
        {/* Delivery funnel ribbon — demand → matching → outcome */}
        <FunnelRibbon period={period} />

        {/* Roles / Delivery / Talent summaries */}
        <GroupCards period={period} />

        {/* Real "needs you" — shortlists awaiting recruiter review */}
        <NeedsReview period={period} />
      </div>
    </DashboardLayout>
  )
}

export const Route = createFileRoute('/dashboard')({ component: Dashboard })
